import axios from 'axios';
import { attachCompanyHeader } from './activeCompany';
import { messagesFromHeader, showMessages } from './messageBox';

// Function to get CSRF token from cookie
const getCSRFToken = () => {
  const name = 'csrftoken=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');

  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i].trim();
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length, cookie.length);
    }
  }
  return '';
};

// Helper function to extract tenant ID from subdomain.
// Mirrors the reserved-subdomain list in apps/tenants/middleware.py.
const RESERVED_SUBDOMAINS = ['www', 'localhost', '127', 'biztimize', 'test', 'dev'];

// Dev-only fallback. The tenant normally comes from the subdomain, so a bare
// `localhost:3000` sends no X-Tenant-ID and TenantMiddleware pins the connection
// to biztimize_master — which holds only the handful of master users. Logging in
// with a tenant account there is a guaranteed 401 ("No active account found"),
// with nothing on screen to say which database was queried. Set
// REACT_APP_TENANT_ID=<identifier> in frontend/.env to work on a tenant from
// plain localhost. Unset (i.e. every deployed environment) changes nothing.
const FALLBACK_TENANT_ID = process.env.REACT_APP_TENANT_ID || null;

const getTenantId = () => {
  const hostname = window.location.hostname;
  const hostnameParts = hostname.split('.');

  // IP addresses and bare hosts carry no tenant
  if (hostnameParts.length < 2 || /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
    return FALLBACK_TENANT_ID;
  }

  const potentialTenant = hostnameParts[0].toLowerCase();
  if (RESERVED_SUBDOMAINS.includes(potentialTenant)) {
    return FALLBACK_TENANT_ID;
  }

  // 'sams.biztimize.com' -> 'sams'. Two-label hosts only carry a tenant when the
  // parent is 'localhost' ('sams.localhost' in dev), never 'biztimize.com'.
  if (hostnameParts.length === 2 && hostnameParts[1].toLowerCase() !== 'localhost') {
    return null;
  }

  return hostnameParts[0];
};

// Create an Axios instance with default config
// Use relative URLs that will be handled by the React proxy in development
const api = axios.create({
  baseURL: '', // Use relative URLs - proxy will handle routing
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies/session
});

// Add request interceptor to include CSRF token, authentication, and Tenant ID
api.interceptors.request.use(
  (config) => {
    // Get CSRF token from cookie
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }

    // Get Tenant ID
    const tenantId = getTenantId();
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    // The company this tab works in (header switcher) — the server offers only
    // this company in company dropdowns and refuses a write naming another.
    attachCompanyHeader(config);

    // Get token from localStorage (if using JWT)
    const token = localStorage.getItem('token');

    if (token) {
      // Only log warnings for invalid tokens
      if (token.trim() === '') {
        console.warn('Empty token found in localStorage');
      }

      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh lock: prevent concurrent refresh requests from multiple in-flight API calls
// (e.g. TC users with multiple HTMX tabs that all hit 401 at the same time)
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);
const onRefreshed = (token) => { refreshSubscribers.forEach(cb => cb(token)); refreshSubscribers = []; };
const onRefreshFailed = () => { refreshSubscribers = []; };

// Add response interceptor to handle token errors and auto-refresh
api.interceptors.response.use(
  (response) => {
    // Capture license grace-period headers set by LicenseEnforcementMiddleware
    const graceExpiry = response.headers['x-license-grace-expiry'];
    const daysRemaining = response.headers['x-license-days-remaining'];
    if (graceExpiry) {
      window.__licenseGraceExpiry = graceExpiry;
      window.__licenseDaysRemaining = daysRemaining ?? null;
    }
    // Side-outcome messages (apps/core/user_messages.py) — e.g. an approval
    // that reached nobody — go to the shared Message Box, with their error code.
    const serverMessages = messagesFromHeader(response.headers['x-biztimize-messages']);
    if (serverMessages.length) showMessages(serverMessages);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log token-related errors
    if (error.response) {
      console.log('API Error Response:', {
        status: error.response.status,
        url: error.config.url,
        data: error.response.data
      });

      // Check for token validation errors
      if (error.response.status === 401 &&
        error.response.data &&
        (error.response.data.detail === "Given token not valid for any token type" ||
          error.response.data.code === "token_not_valid") &&
        !originalRequest._retry &&
        !originalRequest.url.includes('/auth/token/refresh/')) {

        console.error('Token validation error detected, attempting refresh...');
        originalRequest._retry = true;

        // Legacy mode: refresh token in localStorage. Cookie mode (SECURITY 2.1):
        // HttpOnly cookie sent automatically, so a missing localStorage value is OK.
        const refreshToken = localStorage.getItem('refreshToken');

        // If another request is already refreshing, queue this request until refresh completes
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            });
            // If refresh ultimately fails, reject queued requests too
            // (handled by onRefreshFailed clearing the queue which leaves promises pending;
            //  they will naturally reject when the page navigates to login)
          });
        }

        isRefreshing = true;
        try {
          console.log('Attempting token refresh...');
          const response = await api.post('/auth/token/refresh/', refreshToken ? { refresh: refreshToken } : {});

          const newToken = response.data.access;
          localStorage.setItem('token', newToken);
          if (response.data.refresh) {
            localStorage.setItem('refreshToken', response.data.refresh);
          }

          isRefreshing = false;
          onRefreshed(newToken);

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          console.log('Token refreshed successfully, retrying original request...');
          return api(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          isRefreshing = false;
          onRefreshFailed();

          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');

          window.location.href = '/auth/login';
          return Promise.reject(refreshError);
        }
      }

      // Unauthenticated 401 (no credentials sent at all).
      //
      // The refresh branch above only matches DRF's "token_not_valid" shape, which
      // is what an EXPIRED token produces. A MISSING token produces a different
      // body -- {"detail": "Authentication credentials were not provided."} -- with
      // no `code` field, so it fell through here and the 401 was handed to whatever
      // page made the call. Each page then rendered its own local error ("Failed to
      // load companies.") and the user was stranded on a signed-out page instead of
      // being sent to log in. This is easy to hit in dev because localStorage is
      // per-origin: a token stored on localhost:3000 does not exist on
      // <tenant>.localhost:3000.
      //
      // Guards, in order: never hijack the /auth/* API calls (a wrong password must
      // surface on the login form, and /auth/me/ probes 401 during bootstrap), and
      // never redirect when already on an auth screen -- either would loop.
      if (error.response.status === 401 && !isRefreshing) {
        const requestUrl = originalRequest?.url || '';
        const isAuthEndpoint = requestUrl.includes('/auth/');
        const onAuthScreen = window.location.pathname.startsWith('/auth/');

        if (!isAuthEndpoint && !onAuthScreen) {
          console.warn(
            `Unauthenticated request to ${requestUrl} on origin ${window.location.origin} — ` +
            'no valid credentials for this origin. Redirecting to login.'
          );
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/auth/login';
        }
      }

      // ── Make a refusal legible ────────────────────────────────────────────
      // `HasModuleAccess` answers a 403 with the codename that was missing and
      // the resource it guards, instead of DRF's generic "You do not have
      // permission to perform this action." Normalise it onto the error object
      // so any caller — and `useReferenceData` in particular — can tell the
      // user WHICH permission to ask their administrator for, rather than
      // rendering an empty dropdown that looks like a bug.
      if (error.response.status === 403) {
        const body = error.response.data || {};
        error.permissionDenied = {
          detail: body.detail || 'You do not have permission to view this data.',
          requiredPermission: body.required_permission || null,
          referencePermission: body.reference_permission || null,
          resource: body.resource || null,
          // 'feature_not_licensed' = the tenant has not bought the module
          // (docs/claude/licensing.md) — no role can fix it, only the licence.
          code: body.code || null,
          feature: body.feature || null,
          notLicensed: body.code === 'feature_not_licensed',
        };
      }
    }

    // A stable error code (apps/core/error_codes.py, e.g. BC-LIC-E0001) lets any
    // screen offer "See related Notes" — see components/knowledge/RelatedNotesLink.
    if (error.response?.data && typeof error.response.data === 'object' && error.response.data.error_code) {
      error.errorCode = error.response.data.error_code;
    }

    return Promise.reject(error);
  }
);

// CRM API services with correct paths matching Django URLs
export const leadService = {
  getLeads: (params) => api.get('/crm/api/leads/', { params }),
  getLead: (id) => api.get(`/crm/api/leads/${id}/`),
  createLead: (data) => api.post('/crm/api/leads/', data),
  updateLead: (id, data) => api.put(`/crm/api/leads/${id}/`, data),
  deleteLead: (id) => api.delete(`/crm/api/leads/${id}/`),
  getLeadActivities: (id) => api.get(`/crm/api/leads/${id}/activities/`),
};

export const opportunityService = {
  getOpportunities: (params) => api.get('/crm/api/opportunities/', { params }),
  getOpportunity: (id) => api.get(`/crm/api/opportunities/${id}/`),
  createOpportunity: (data) => api.post('/crm/api/opportunities/', data),
  updateOpportunity: (id, data) => api.put(`/crm/api/opportunities/${id}/`, data),
  deleteOpportunity: (id) => api.delete(`/crm/api/opportunities/${id}/`),
  getOpportunityActivities: (id) => api.get(`/crm/api/opportunities/${id}/activities/`),
};

export const customerService = {
  getCustomers: (params) => api.get('/crm/api/customers/', { params }),
  getCustomer: (id) => api.get(`/crm/api/customers/${id}/`),
  createCustomer: (data) => api.post('/crm/api/customers/', data),
  updateCustomer: (id, data) => api.put(`/crm/api/customers/${id}/`, data),
  deleteCustomer: (id) => api.delete(`/crm/api/customers/${id}/`),
  getCustomerAddresses: (id) => api.get(`/crm/api/customers/${id}/addresses/`),
  getCustomerContacts: (id) => api.get(`/crm/api/customers/${id}/contacts/`),
};

export const quotationService = {
  getQuotations: (params) => api.get('/crm/api/quotations/', { params }),
  getQuotation: (id) => api.get(`/crm/api/quotations/${id}/`),
  createQuotation: (data) => api.post('/crm/api/quotations/', data),
  updateQuotation: (id, data) => api.put(`/crm/api/quotations/${id}/`, data),
  deleteQuotation: (id) => api.delete(`/crm/api/quotations/${id}/`),
  getQuotationItems: (id) => api.get(`/crm/api/quotations/${id}/items/`),
};

export const salesOrderService = {
  getSalesOrders: (params) => api.get('/crm/api/sales-orders/', { params }),
  getSalesOrder: (id) => api.get(`/crm/api/sales-orders/${id}/`),
  createSalesOrder: (data) => api.post('/crm/api/sales-orders/', data),
  updateSalesOrder: (id, data) => api.put(`/crm/api/sales-orders/${id}/`, data),
  deleteSalesOrder: (id) => api.delete(`/crm/api/sales-orders/${id}/`),
  getSalesOrderItems: (id) => api.get(`/crm/api/sales-orders/${id}/items/`),
  getSalesOrderActivities: (id) => api.get(`/crm/api/sales-orders/${id}/activities/`),
};

// Dashboard services
export const dashboardService = {
  getStats: () => api.get('/crm/api/dashboard/stats/'),
  getRecentLeads: (limit = 5) => api.get(`/crm/api/leads/?limit=${limit}&ordering=-created_at`),
  getRecentOpportunities: (limit = 5) => api.get(`/crm/api/opportunities/?limit=${limit}&ordering=-created_at`),
  getSalesPerformance: (period = '30d') => api.get(`/crm/api/dashboard/sales-performance/?period=${period}`),
};

// Delivery services
export const deliveryService = {
  getDeliveryNotes: (params) => api.get('/crm/api/delivery-notes/', { params }),
  getDeliveryNote: (id) => api.get(`/crm/api/delivery-notes/${id}/`),
  createDeliveryNote: (data) => api.post('/crm/api/delivery-notes/', data),
  updateDeliveryNote: (id, data) => api.put(`/crm/api/delivery-notes/${id}/`, data),
  deleteDeliveryNote: (id) => api.delete(`/crm/api/delivery-notes/${id}/`),
  getDeliveryNoteItems: (id) => api.get(`/crm/api/delivery-notes/${id}/items/`),
  createDeliveryNoteItem: (data) => api.post('/crm/api/delivery-note-items/', data),
  updateDeliveryNoteItem: (id, data) => api.patch(`/crm/api/delivery-note-items/${id}/`, data),
  createInvoiceFromDeliveryNotes: (deliveryNoteIds) =>
    api.post('/crm/api/delivery-notes/create-invoice/', { delivery_note_ids: deliveryNoteIds }),
};

// Sales Return services
export const returnService = {
  getReturns: (params) => api.get('/crm/api/returns/', { params }),
  getReturn: (id) => api.get(`/crm/api/returns/${id}/`),
  createReturn: (data) => api.post('/crm/api/returns/', data),
  updateReturn: (id, data) => api.put(`/crm/api/returns/${id}/`, data),
  patchReturn: (id, data) => api.patch(`/crm/api/returns/${id}/`, data),
  deleteReturn: (id) => api.delete(`/crm/api/returns/${id}/`),
  submitReturn: (id, data) => api.post(`/crm/api/returns/${id}/submit/`, data || {}),
  inspectReturn: (id, data) => api.post(`/crm/api/returns/${id}/inspect/`, data),
  approveReturn: (id, data) => api.post(`/crm/api/returns/${id}/approve/`, data || {}),
  rejectReturn: (id, data) => api.post(`/crm/api/returns/${id}/reject/`, data || {}),
  issueCreditNote: (id, data) => api.post(`/crm/api/returns/${id}/issue_credit_note/`, data || {}),
  createReplacement: (id, data) => api.post(`/crm/api/returns/${id}/create_replacement/`, data || {}),
  closeReturn: (id, data) => api.post(`/crm/api/returns/${id}/close/`, data || {}),
  getActivities: (id) => api.get(`/crm/api/returns/${id}/activities/`),
  getReturnReasons: (params) => api.get('/crm/api/return-reasons/', { params }),
};

// Invoice services
export const invoiceService = {
  getInvoices: (params) => api.get('/crm/api/invoices/', { params }),
  getInvoice: (id) => api.get(`/crm/api/invoices/${id}/`),
  createInvoice: (data) => api.post('/crm/api/invoices/', data),
  updateInvoice: (id, data) => api.put(`/crm/api/invoices/${id}/`, data),
  deleteInvoice: (id) => api.delete(`/crm/api/invoices/${id}/`),
  getInvoiceItems: (id) => api.get(`/crm/api/invoices/${id}/items/`),
  getInvoiceTypes: (params) => api.get('/crm/api/invoice-types/', { params }),
  createInvoiceItem: (data) => api.post('/crm/api/invoice-items/', data),
  updateInvoiceItem: (id, data) => api.put(`/crm/api/invoice-items/${id}/`, data),
  deleteInvoiceItem: (id) => api.delete(`/crm/api/invoice-items/${id}/`),
  recalculateInvoice: (id) => api.post(`/crm/api/invoices/${id}/recalculate/`),

  // Customs invoice (export layout of the same invoice record)
  getExportPorts: () => api.get('/crm/api/invoices/ports/'),
  getCustomsReadiness: (id) => api.get(`/crm/api/invoices/${id}/customs-readiness/`),
  stampCustomsRate: (id, { force = false } = {}) =>
    api.post(`/crm/api/invoices/${id}/stamp-customs-rate/`, null, {
      params: force ? { force: 1 } : {},
    }),
  customsInvoicePrintUrl: (id, { pdf = false } = {}) =>
    `/crm/invoice/${id}/customs-invoice/${pdf ? '?format=pdf' : ''}`,
  customsInvoiceExcelUrl: (id) => `/crm/invoice/${id}/customs-invoice/excel/`,

  // Export invoices only — the Customs Invoice worklist. Filtered on the
  // server by the same flag apps.exim treats as "this is an export"; never
  // filter a loaded invoice list in the client for this.
  getExportInvoices: (params = {}) =>
    api.get('/crm/api/invoices/', {
      params: { ...params, invoice_type__is_export_type: true },
    }),
};

// Customer Payment services (fi_ar)
export const paymentService = {
  getPayments: (params) => api.get('/fi_ar/api/customer-payments/', { params }),
  getPayment: (id) => api.get(`/fi_ar/api/customer-payments/${id}/`),
  createPayment: (data) => api.post('/fi_ar/api/customer-payments/', data),
  updatePayment: (id, data) => api.put(`/fi_ar/api/customer-payments/${id}/`, data),
  patchPayment: (id, data) => api.patch(`/fi_ar/api/customer-payments/${id}/`, data),
  deletePayment: (id) => api.delete(`/fi_ar/api/customer-payments/${id}/`),
  getPaymentLookups: () => api.get('/fi_ar/api/customer-payments/lookups/'),
  postPayment: (id) => api.post(`/fi_ar/api/customer-payments/${id}/post_payment/`),
  // A reason is MANDATORY — the server refuses without one, because reversing a
  // receipt posts a contra journal and re-opens the invoices it settled. Prefer
  // `paymentsReceivedService.reversePayment` + `ReverseReceiptDialog`, which
  // collect it and report what the reversal did.
  reversePayment: (id, payload = {}) =>
    api.post(`/fi_ar/api/customer-payments/${id}/reverse_payment/`, payload),
};

// Search services
export const searchService = {
  // Unified search across modules
  search: (params, config = {}) => api.get('/api/search/', { params, ...config }),
  searchAdvanced: (searchData, config = {}) => api.post('/api/search/', searchData, config),

  // Search health and status
  getSearchHealth: (config = {}) => api.get('/api/search/health/', config),

  // Search suggestions and facets
  getSuggestions: (partialQuery, config = {}) => api.get('/api/search/suggestions/', {
    params: { q: partialQuery },
    ...config
  }),
  getFacets: (params, config = {}) => api.get('/api/search/facets/', { params, ...config }),

  // Document-specific search
  searchDocuments: (params) => api.get('/core/api/documents/search/', { params }),
  searchProducts: (params) => api.get('/mm/api/products/search/', { params }),

  // Persisted per-user search history (stored on UserNavigationPreference.search_history)
  getSearchHistory: () => api.get('/users/api/navigation-preferences/'),
  saveSearchHistory: (history) =>
    api.post('/users/api/navigation-preferences/', { search_history: history }),
};

// Document Management services
export const documentService = {
  // Get document details
  getDocument: (id) => {
    return api.get(`/core/api/documents/${id}/`);
  },

  getDocumentContent: (id) => {
    return api.get(`/core/api/documents/${id}/content/`);
  },

  downloadDocument: (id) => {
    return api.get(`/core/api/documents/${id}/download/`, { responseType: 'blob' });
  },

  // Document metadata and related documents
  getDocumentMetadata: (id) => {
    return api.get(`/core/api/documents/${id}/metadata/`);
  },

  getRelatedDocuments: (id) => {
    return api.get(`/core/api/documents/${id}/related/`);
  },

  // Document versions
  getDocumentVersions: (id) => {
    return api.get(`/core/api/documents/${id}/versions/`);
  },

  getDocumentVersion: (id, versionId) => {
    return api.get(`/core/api/documents/${id}/versions/${versionId}/`);
  },
};

// Tasks Management services
export const tasksService = {
  // Projects
  getProjects: (params) => api.get('/tasks/api/projects/', { params }),
  getProject: (id) => api.get(`/tasks/api/projects/${id}/`),
  createProject: (data) => api.post('/tasks/api/projects/', data),
  updateProject: (id, data) => api.put(`/tasks/api/projects/${id}/`, data),
  deleteProject: (id) => api.delete(`/tasks/api/projects/${id}/`),
  syncProjectTemplate: (id) => api.post(`/tasks/api/projects/${id}/sync-template/`),
  // Tasks Replanning: the project + its tasks, and preview (dry_run) / apply of
  // duration changes - { changes: [{ task_id, duration_days }], dry_run, reason }.
  getProjectReplanning: (id) => api.get(`/tasks/api/projects/${id}/replanning/`),
  replanProject: (id, data) => api.post(`/tasks/api/projects/${id}/replan/`, data),
  // Transfer Employee: the From/To pickers (with task counts), one employee's tasks,
  // and the move - { from_employee, to_employee, task_ids (omit = all open), reason }.
  getTransferCandidates: () => api.get('/tasks/api/tasks/transfer-candidates/'),
  getTransferTasks: (params) => api.get('/tasks/api/tasks/transfer-tasks/', { params }),
  transferEmployeeTasks: (data) => api.post('/tasks/api/tasks/transfer-employee/', data),

  // Tasks
  getTasks: (params) => api.get('/tasks/api/tasks/', { params }),
  getTask: (id) => api.get(`/tasks/api/tasks/${id}/`),
  createTask: (data) => api.post('/tasks/api/tasks/', data),
  updateTask: (id, data) => api.put(`/tasks/api/tasks/${id}/`, data),
  // PATCH, not PUT: the Excel-style grid at /tasks/excel sends only the cells that
  // actually changed, and a PUT would blank every field the payload omits.
  patchTask: (id, data) => api.patch(`/tasks/api/tasks/${id}/`, data),
  deleteTask: (id) => api.delete(`/tasks/api/tasks/${id}/`),
  assignTask: (data) => api.post('/tasks/api/assign-task/', data),

  // Tasks Upload (TA template workbook -> project tasks).
  // The SAME file is posted to preview and again to commit: the server re-parses
  // and re-validates on commit rather than trusting the report it handed back,
  // so the browser never sends a row list. Hence the file, not the preview, is
  // what the page holds on to between the two steps.
  downloadTasksUploadSample: (project) => api.get('/tasks/api/tasks-upload/sample/', { params: project ? { project } : {}, responseType: 'blob' }),
  previewTasksUpload: (file, options = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (options.onDuplicate) form.append('on_duplicate', options.onDuplicate);
    if (options.sheet) form.append('sheet', options.sheet);
    return api.post('/tasks/api/tasks-upload/preview/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: options.onUploadProgress,
    });
  },
  commitTasksUpload: (file, options = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (options.onDuplicate) form.append('on_duplicate', options.onDuplicate);
    if (options.sheet) form.append('sheet', options.sheet);
    return api.post('/tasks/api/tasks-upload/commit/', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Task Dependencies
  getTaskDependencies: (taskId) => api.get(`/tasks/api/tasks/${taskId}/dependencies/`),
  addTaskDependency: (taskId, dependencyData) => api.post(`/tasks/api/tasks/${taskId}/dependencies/`, dependencyData),
  removeTaskDependency: (taskId, dependencyId) => api.delete(`/tasks/api/tasks/${taskId}/dependencies/${dependencyId}/`),

  // Task Comments
  getTaskComments: (taskId) => api.get(`/tasks/api/tasks/${taskId}/comments/`),
  addTaskComment: (taskId, comment) => api.post(`/tasks/api/tasks/${taskId}/comments/`, comment),
  updateTaskComment: (taskId, commentId, comment) => api.put(`/tasks/api/tasks/${taskId}/comments/${commentId}/`, comment),
  deleteTaskComment: (taskId, commentId) => api.delete(`/tasks/api/tasks/${taskId}/comments/${commentId}/`),

  // Recurring Task Definitions
  getRecurringDefinitions: (params) => api.get('/tasks/api/recurring-definitions/', { params }),
  getRecurringDefinition: (id) => api.get(`/tasks/api/recurring-definitions/${id}/`),
  createRecurringDefinition: (data) => api.post('/tasks/api/recurring-definitions/', data),
  updateRecurringDefinition: (id, data) => api.put(`/tasks/api/recurring-definitions/${id}/`, data),
  patchRecurringDefinition: (id, data) => api.patch(`/tasks/api/recurring-definitions/${id}/`, data),
  deleteRecurringDefinition: (id) => api.delete(`/tasks/api/recurring-definitions/${id}/`),
  toggleRecurringDefinition: (id) => api.post(`/tasks/api/recurring-definitions/${id}/toggle_enabled/`),
  generateRecurringTaskNow: (id) => api.post(`/tasks/api/recurring-definitions/${id}/generate_now/`),

  // Resource Capacity / Workload
  getCapacitySummary: (params) => api.get('/tasks/api/capacity/summary/', { params }),
  getCapacityHeatmap: (params) => api.get('/tasks/api/capacity/heatmap/', { params }),
  getCapacityAlerts: (params) => api.get('/tasks/api/capacity/alerts/', { params }),

  // Task Templates
  getTaskTemplates: (params) => api.get('/tasks/api/templates/', { params }),
  getTaskTemplate: (id) => api.get(`/tasks/api/templates/${id}/`),
  createTaskTemplate: (data) => api.post('/tasks/api/templates/', data),
  duplicateTaskTemplate: (id) => api.post(`/tasks/api/templates/${id}/duplicate/`),
  updateTaskTemplate: (id, data) => api.put(`/tasks/api/templates/${id}/`, data),
  deleteTaskTemplate: (id) => api.delete(`/tasks/api/templates/${id}/`),

  // Task Definitions
  getTaskDefinitions: (params) => api.get('/tasks/api/task-definitions/', { params }),
  getTaskDefinition: (id) => api.get(`/tasks/api/task-definitions/${id}/`),
  createTaskDefinition: (data) => api.post('/tasks/api/task-definitions/', data),
  updateTaskDefinition: (id, data) => api.put(`/tasks/api/task-definitions/${id}/`, data),
  deleteTaskDefinition: (id) => api.delete(`/tasks/api/task-definitions/${id}/`),

  // Task Definition Dependencies
  getTaskDefinitionDependencies: (params) => api.get('/tasks/api/task-definition-dependencies/', { params }),
  createTaskDefinitionDependency: (data) => api.post('/tasks/api/task-definition-dependencies/', data),
  updateTaskDefinitionDependency: (id, data) => api.put(`/tasks/api/task-definition-dependencies/${id}/`, data),
  deleteTaskDefinitionDependency: (id) => api.delete(`/tasks/api/task-definition-dependencies/${id}/`),

  // Categories and metadata
  getTaskCategories: (params) => api.get('/tasks/api/categories/', { params }),
  getCategories: (params) => api.get('/tasks/api/categories/', { params }).then(res => res.data?.results || res.data || []),
  getTaskPriorities: (params) => api.get('/tasks/api/priorities/', { params }),
  getPriorities: (params) => api.get('/tasks/api/priorities/', { params }).then(res => res.data?.results || res.data || []),
  getTaskSources: (params) => api.get('/tasks/api/sources/', { params }),

  // Employees (for task assignment)
  getEmployees: (params) => api.get('/hrm/api/employees/', { params }).then(res => res.data?.results || res.data || []),

  // Dashboard and Analytics
  getDashboardData: (params) => api.get('/tasks/api/tasks/dashboard_data/', { params }),
  getTaskAnalytics: (params) => api.get('/tasks/api/analytics/', { params }),

  // Time spent (dashboard grid's editable "Time Spent" cell)
  getTaskTimeEntries: (taskId) => api.get(`/tasks/api/tasks/${taskId}/time-entries/`),
  addTaskComment: (taskId, comment) => api.post(`/tasks/api/tasks/${taskId}/add-comment/`, { comment }),
  adjustTaskTime: (taskId, data) => api.post(`/tasks/api/tasks/${taskId}/adjust-time/`, data),

  // Bulk operations
  bulkUpdateTasks: (data) => api.post('/tasks/api/tasks/bulk_update/', data),
  bulkDeleteTasks: (taskIds) => api.post('/tasks/api/tasks/bulk_delete/', { task_ids: taskIds }),

  // Export functionality
  exportTasks: (format, params) => api.get(`/tasks/api/export/${format}/`, {
    params,
    responseType: 'blob'
  }),
  // Streaming Excel export of ALL filtered tasks (no pagination cap).
  // Honors the same row-level auth + `view`/status/search filters as the list grid.
  exportTasksXlsx: (params) => api.get('/tasks/api/tasks/export/', {
    params,
    responseType: 'blob'
  }),
  exportProjects: (format, params) => api.get(`/tasks/api/export/projects/${format}/`, {
    params,
    responseType: 'blob'
  }),

  // Real-time data
  getKPIs: () => api.get('/tasks/api/kpis/'),
  getChartData: (chartType, params) => api.get(`/tasks/api/charts/${chartType}/`, { params }),

  // Cost report
  getCostReport: (params) => api.get('/tasks/api/tasks/cost_report/', { params }),
};

// Dispute Management services
export const disputeService = {
  // CRUD
  getDisputes: (params) => api.get('/api/dispute-management/disputes/', { params }),
  getDispute: (id) => api.get(`/api/dispute-management/disputes/${id}/`),
  createDispute: (data) => api.post('/api/dispute-management/disputes/', data),
  updateDispute: (id, data) => api.patch(`/api/dispute-management/disputes/${id}/`, data),
  deleteDispute: (id) => api.delete(`/api/dispute-management/disputes/${id}/`),

  // Lifecycle transitions
  acknowledge: (id) => api.post(`/api/dispute-management/disputes/${id}/acknowledge/`),
  startInvestigation: (id) => api.post(`/api/dispute-management/disputes/${id}/start-investigation/`),
  proposeResolution: (id, data) => api.post(`/api/dispute-management/disputes/${id}/propose-resolution/`, data),
  approve: (id) => api.post(`/api/dispute-management/disputes/${id}/approve/`),
  rejectResolution: (id, data) => api.post(`/api/dispute-management/disputes/${id}/reject-resolution/`, data),
  markResolved: (id, data) => api.post(`/api/dispute-management/disputes/${id}/mark-resolved/`, data),
  close: (id) => api.post(`/api/dispute-management/disputes/${id}/close/`),
  cancel: (id, data) => api.post(`/api/dispute-management/disputes/${id}/cancel/`, data),

  // Sub-resources
  getComments: (id) => api.get(`/api/dispute-management/disputes/${id}/comments/`),
  addComment: (id, data) => api.post(`/api/dispute-management/disputes/${id}/comments/`, data),
  getAttachments: (id) => api.get(`/api/dispute-management/disputes/${id}/attachments/`),
  uploadAttachment: (id, formData) => api.post(
    `/api/dispute-management/disputes/${id}/attachments/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),

  // Dashboard
  getDashboardStats: () => api.get('/api/dispute-management/disputes/dashboard/stats/'),
};

// Journal Entry services
export const journalEntryService = {
  getJournalEntries: (params) => api.get('/financial_accounting/api/journal-entries/', { params }),
  getJournalEntry: (id) => api.get(`/financial_accounting/api/journal-entries/${id}/`),
  getJournalEntryLines: (id) => api.get(`/financial_accounting/api/journal-entries/${id}/lines/`),
  createJournalEntry: (data) => api.post('/financial_accounting/api/journal-entries/', data),
  updateJournalEntry: (id, data) => api.put(`/financial_accounting/api/journal-entries/${id}/`, data),
  patchJournalEntry: (id, data) => api.patch(`/financial_accounting/api/journal-entries/${id}/`, data),
  deleteJournalEntry: (id) => api.delete(`/financial_accounting/api/journal-entries/${id}/`),
  postJournal: (id) => api.post(`/financial_accounting/api/journal-entries/${id}/post_journal/`),
  reverseJournal: (id, data) => api.post(`/financial_accounting/api/journal-entries/${id}/reverse/`, data),
  submitForApproval: (id) => api.post(`/financial_accounting/api/journal-entries/${id}/submit_for_approval/`),
  approveJournal: (id) => api.post(`/financial_accounting/api/journal-entries/${id}/approve/`),
  rejectJournal: (id) => api.post(`/financial_accounting/api/journal-entries/${id}/reject/`),
  // `params` may carry { company } to scope gl_accounts to one chart of accounts.
  getLookups: (params) => api.get('/financial_accounting/api/journal-entries/lookups/', { params }),
  getSummaryStats: () => api.get('/financial_accounting/api/journal-entries/summary_stats/'),
};

// Authentication services
export const authService = {
  login: async (credentials) => {
    console.log('authService: Attempting login with credentials (using Axios):', credentials);
    try {
      const response = await api.post('/auth/token/', credentials);
      // These logs will now be very important to confirm Axios behaves as expected
      console.log('authService: Axios - Raw response from api.post:', response);
      console.log('authService: Axios - response.data from api.post:', response.data);
      return response;
    } catch (error) {
      console.error('authService: Axios - Error in login api.post:', error.response || error.message || error);
      throw error;
    }
  },
  // Legacy mode sends the refresh token in the body; cookie mode (SECURITY 2.1)
  // sends an empty body and the backend reads the HttpOnly cookie.
  refreshToken: (refresh) => api.post('/auth/token/refresh/', refresh ? { refresh } : {}),
  register: (userData) => api.post('/auth/register/', userData),
  getCurrentUser: () => api.get('/auth/me/'),

  // Public: the sign-in page asks for the tenant's logo before anyone is
  // logged in. Never let a failure here block the login form.
  getTenantBranding: () => api.get('/auth/branding/'),

  // Force change password (first login)
  forceChangePassword: (currentPassword, newPassword) =>
    api.post('/auth/password/force-change/', { current_password: currentPassword, new_password: newPassword }),

  // Password reset endpoints
  forgotPassword: (email) => api.post('/auth/password/reset/', { email }),
  validateResetToken: (token, uid) => api.post('/auth/password/reset/validate/', { token, uid }),
  resetPassword: (token, uid, password) => api.post('/auth/password/reset/confirm/', { token, uid, password }),

  // Multi-Factor Authentication (TOTP)
  // Setup endpoints accept either an mfa_token (first-time login) or rely on the
  // Authorization header (opting in from the security page).
  mfaSetupBegin: (mfaToken) => api.post('/auth/mfa/setup/begin/', mfaToken ? { mfa_token: mfaToken } : {}),
  mfaSetupConfirm: (otp, mfaToken) =>
    api.post('/auth/mfa/setup/confirm/', mfaToken ? { otp, mfa_token: mfaToken } : { otp }),
  mfaVerify: (mfaToken, otp) => api.post('/auth/mfa/verify/', { mfa_token: mfaToken, otp }),
  mfaStatus: () => api.get('/auth/mfa/status/'),
  mfaDisable: (otp) => api.post('/auth/mfa/disable/', { otp }),
};

// Report Field Labels (Administrator menu): per-screen column captions for every
// grid. GatedGrid reads the map through services/screenLabels.js.
export const screenLabelsService = {
  list: (params) => api.get('/api/field-status/labels/', { params }),
  create: (data) => api.post('/api/field-status/labels/', data),
  update: (id, data) => api.patch(`/api/field-status/labels/${id}/`, data),
  remove: (id) => api.delete(`/api/field-status/labels/${id}/`),
  columns: (params) => api.get('/api/field-status/labels/columns/', { params }),
};

export default api;